import { useRef } from "react";
import styled from "styled-components";

import { selectHeight, selectHeightSmall } from "../store/constants";
import { Transition } from "src/utils";
import { Color, Flex, Icon, Text } from "src/elements";
import { IconType } from "src/elements/Icon";
import { useHoverClickable } from "src/utils/hoverClickable";
import { styledSafe } from "src/utils/styledComponent";

const PinButton = styledSafe("button")<{ isVisible: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 14px;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};

  &:focus-visible {
    opacity: 1;
    outline: 1px solid ${Color.primary};
    outline-offset: 2px;
  }

  @media (hover: none) {
    opacity: 1;
  }
`;

const OptionSelectTarget = styled(Flex)`
  flex: 1;
  min-width: 0;
  align-self: stretch;
  align-items: center;
  justify-content: space-between;
  gap: 5px;
`;

const OptionElement = styled(Flex)<{
  isHovered: boolean;
  isActive: boolean;
  isSelected: boolean;
  isSelectable: boolean;
  isOpener: boolean;
  size?: "small";
}>`
  gap: 5px;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  min-height: ${({ size }) =>
    size === "small" ? `${selectHeightSmall - 2}px` : `${selectHeight - 2}px`};
  padding: ${({ size }) => (size === "small" ? "2px 3px" : "7px 10px")};
  user-select: none;
  cursor: pointer;
  transition: background-color ${Transition.fast};

  pointer-events: ${({ isSelectable }) => !isSelectable && "none"};
  background-color: ${({ isHovered, isActive, isSelected, isOpener }) =>
    (isOpener && Color.white) ||
    (isSelected && Color.greenSmoke) ||
    (isActive && Color.greenSmokeHover) ||
    (isHovered && Color.lightGray)};
`;

interface Props<T> {
  label: string;
  value?: T;
  icon?: {
    name: IconType;
    color?: string;
  };
  leftIcon?: {
    name: IconType;
    color?: string;
  };
  size?: "small";
  isSelected?: boolean;
  isSelectable?: boolean;
  isPlaceholder?: boolean;
  isDisabled?: boolean;
  isOpener?: boolean;
  onSelect?: (value: T) => void;
  pin?: { isPinned: boolean; actionLabel: string; onToggle: () => void };
}

export const Option = <T,>({
  label,
  value,
  icon,
  leftIcon,
  size,
  isSelected = false,
  isSelectable = true,
  isPlaceholder = false,
  isDisabled = false,
  isOpener = false,
  onSelect,
  pin,
}: Props<T>) => {
  const refOption = useRef<HTMLDivElement>(null);
  const { isHovered, isActive } = useHoverClickable(refOption);

  const onClick = () => {
    if (!isDisabled && onSelect && value !== undefined) {
      onSelect(value);
    }
  };

  return (
    <OptionElement
      ref={refOption}
      isHovered={isHovered}
      isActive={isActive}
      isSelected={isSelected}
      isSelectable={isSelectable}
      isOpener={isOpener}
      size={size}
      role={isOpener ? undefined : "presentation"}
    >
      <OptionSelectTarget
        onClick={onClick}
        role={isOpener ? undefined : "option"}
        aria-selected={isOpener ? undefined : isSelected}
        aria-disabled={isDisabled || !isSelectable}
      >
        <Flex align="center" gap="5px" style={{ overflow: "hidden" }}>
          {leftIcon && (
            <Flex minWidth="14px">
              <Icon name={leftIcon.name} size={14} color={leftIcon.color} />
            </Flex>
          )}
          <Text
            whiteSpace={isOpener ? "nowrap" : "wrap"}
            textOverflow={isOpener ? "ellipsis" : undefined}
            variant={size === "small" ? "caption1" : isSelected ? "body1" : "body3"}
            color={
              (isSelected && isDisabled && Color.greenSmokeActive) ||
              (isDisabled && Color.textDisabled) ||
              (isPlaceholder && Color.spaceGray) ||
              (!isSelectable && Color.spaceGray) ||
              (isSelected && Color.primaryActive) ||
              (isActive && Color.primaryActive) ||
              (isHovered && Color.textMain) ||
              Color.textMain
            }
          >
            {label}
          </Text>
        </Flex>

        {icon && (
          <Flex minWidth="14px">
            <Icon name={icon.name} size={14} color={icon.color} />
          </Flex>
        )}
      </OptionSelectTarget>

      {pin && (
        <PinButton
          type="button"
          isVisible={pin.isPinned || isHovered}
          data-visible={pin.isPinned || isHovered}
          aria-label={pin.actionLabel}
          title={pin.actionLabel}
          onClick={(event) => {
            event.stopPropagation();
            pin.onToggle();
          }}
        >
          <Icon
            name={pin.isPinned ? "pinFilled" : "pin"}
            size={14}
            color={pin.isPinned ? Color.primary : Color.spaceGray}
          />
        </PinButton>
      )}
    </OptionElement>
  );
};
